import format from 'date-fns/format';
import { TableChangesProps } from '../../components/MaterialTable/MaterialTable.model';
import {
  GetProjectsOverviewProjectOverviewFiltersParameter,
  GetProjectsPaginationParameter,
  GetProjectsProjectFiltersParameter,
  GetUsersPageUserFiltersParameter,
  ProjectExportRequestDto
} from '../../build/generated-sources/openapi';
import dayjs from 'dayjs';

export const stringToDate = (dateString?: string): Date | undefined => {
  // Defenzivně: pokud sem doteče něco jiného než řetězec (např. datum serializované jako
  // JSON pole z Java LocalDate u nesprávně otypovaného pole), nezhroutíme se na `.split`,
  // jen vrátíme undefined (pole se tváří jako prázdné) místo pádu celé karty.
  if (!dateString || typeof dateString !== 'string') {
    return undefined;
  }
  const [year, month, day] = dateString.split('-').map(Number);

  return new Date(year, month - 1, day);
};

export const stringDateToFormattedString = (dateString: any): string => {
  if (!dateString) {
    return '';
  }
  // @ts-ignore
  return format(stringToDate(dateString), 'dd.MM.yyyy');
};

export const dateToFormattedStringForSave = (date?: Date): string => {
  if (!date) {
    return '';
  }
  return format(date, 'yyyy-MM-dd');
};

export const stringToDateTime = (dateTimeString?: string): Date | undefined => {
  if (!dateTimeString) {
    return undefined;
  }
  const [datePart, timePart] = dateTimeString.split(' ');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute, second] = timePart.split(':').map(Number);
  return new Date(year, month - 1, day, hour, minute, second);
};

export const stringDateTimeToFormattedString = (dateTimeString: any): string => {
  if (!dateTimeString) {
    return '';
  }
  // @ts-ignore
  return format(stringToDateTime(dateTimeString), 'dd.MM.yyyy HH:mm');
};

export const dateTimeToFormattedStringForSave = (dateTime?: Date): string => {
  if (!dateTime) {
    return '';
  }
  return format(dateTime, 'yyyy-MM-dd HH:mm');
};

export const formatDateTime = (value?: string): string => {
  if (!value) return '';
  return dayjs(value).format('DD.MM.YYYY HH:mm');
};

export const getProjectsOverviewFiltersParameter = (
  overviewFilters: any[],
  filterMyProjects: boolean
): GetProjectsProjectFiltersParameter => {
  return JSON.stringify({
    overviewFilters: overviewFilters,
    filterMyProjects: filterMyProjects
  }) as GetProjectsOverviewProjectOverviewFiltersParameter;
};

export const getProjectsTableFiltersParameter = (
  tableChanges: TableChangesProps,
  filterMyProjects: boolean
): GetProjectsProjectFiltersParameter => {
  const replacer = (key: string, value: any) => {
    if (
      typeof value === 'string' &&
      !isNaN(Date.parse(value)) &&
      dayjs(value, 'd. M. yyyy h:mm:ss').isValid()
    ) {
      // [DAP-021] Normalizace exotických mezer: některé prohlížeče (ICU) vkládají do cs-CZ
      // formátu úzkou/nezlomitelnou mezeru (U+202F / U+00A0). Backendový parser
      // „d. M. yyyy H:mm:ss" má ale literálně obyčejné mezery, takže by parse spadl (500).
      // Převedeme je proto na obyčejnou mezeru, ať filtr funguje napříč prohlížeči.
      return new Date(value)
        .toLocaleString('cs-CZ', { timeZone: 'Europe/Prague' })
        .replace(/[\u202f\u00a0]/g, ' ');
    }
    return value;
  };
  return JSON.stringify(
    {
      globalFilter: tableChanges.globalFilter,
      columnFilters: tableChanges.columnFilters,
      filterMyProjects: filterMyProjects
    },
    replacer
  ) as GetProjectsProjectFiltersParameter;
};

export const getProjectsTablePaginationParameter = (
  tableChanges: TableChangesProps
): GetProjectsPaginationParameter => {
  return JSON.stringify({
    sorting: tableChanges.sorting,
    pageIndex: tableChanges.pagination.pageIndex,
    pageSize: tableChanges.pagination.pageSize
  }) as GetProjectsPaginationParameter;
};

// --- Persistence stavu tabulky projektů do URL (DAP-016) ---

// Název URL query parametru, do kterého serializujeme celý stav tabulky projektů.
export const PROJECTS_TABLE_STATE_PARAM = 'stav';

// Serializuje stav tabulky (filtry vč. dlaždic, řazení, stránkování) do jednoho
// kompaktního JSON řetězce vhodného pro URL. Vrací undefined, pokud je stav výchozí
// (žádné filtry, žádné řazení, první stránka, výchozí velikost stránky, prázdné hledání) —
// URL pak zůstává čistá.
export const serializeProjectsTableState = (
  tableChanges: TableChangesProps
): string | undefined => {
  const isDefault =
    (tableChanges.columnFilters?.length ?? 0) === 0 &&
    (tableChanges.sorting?.length ?? 0) === 0 &&
    (tableChanges.pagination?.pageIndex ?? 0) === 0 &&
    (tableChanges.pagination?.pageSize ?? 10) === 10 &&
    !tableChanges.globalFilter;

  if (isDefault) {
    return undefined;
  }

  return JSON.stringify({
    columnFilters: tableChanges.columnFilters ?? [],
    sorting: tableChanges.sorting ?? [],
    pageIndex: tableChanges.pagination?.pageIndex ?? 0,
    pageSize: tableChanges.pagination?.pageSize ?? 10,
    globalFilter: tableChanges.globalFilter ?? ''
  });
};

// Deserializuje stav tabulky z URL řetězce. Robustní vůči neznámým/nevalidním hodnotám —
// při jakékoli chybě vrací null a volající použije výchozí stav (stránka nespadne).
export const deserializeProjectsTableState = (
  raw: string | null | undefined
): TableChangesProps | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }

    const columnFilters = Array.isArray(parsed.columnFilters)
      ? parsed.columnFilters.filter(
          (filter: any) => filter && typeof filter === 'object' && typeof filter.id === 'string'
        )
      : [];

    const sorting = Array.isArray(parsed.sorting)
      ? parsed.sorting.filter(
          (sort: any) => sort && typeof sort === 'object' && typeof sort.id === 'string'
        )
      : [];

    const pageIndexRaw = Number(parsed.pageIndex);
    const pageIndex = Number.isFinite(pageIndexRaw) && pageIndexRaw >= 0 ? pageIndexRaw : 0;

    const pageSizeRaw = Number(parsed.pageSize);
    const pageSize = Number.isFinite(pageSizeRaw) && pageSizeRaw > 0 ? pageSizeRaw : 10;

    const globalFilter = typeof parsed.globalFilter === 'string' ? parsed.globalFilter : '';

    return {
      columnFilters,
      sorting,
      pagination: { pageIndex, pageSize },
      globalFilter
    };
  } catch {
    return null;
  }
};

// [P1] Perzistence stavu tabulky do sessionStorage byla odstraněna — stav se drží pouze v URL
// (viz serializeProjectsTableState / PROJECTS_TABLE_STATE_PARAM), abychom se vyhnuli riziku
// „rozbité" browser storage.

// Mapping between Material React Table column accessorKeys and backend field names
const COLUMN_MAPPING: Record<string, string> = {
  name: 'name',
  registrationNumber: 'registrationNumber',
  holderIc: 'holderIc',
  holder: 'holder',
  holderLegalForm: 'holderLegalFormTranslation',
  createdBy: 'createdBy',
  updatedBy: 'updatedBy',
  eventNumber: 'eventNumber',
  guarantor: 'guarantor',
  contactPerson: 'contactPerson',
  contactEmails: 'contactEmails',
  manager: 'manager',
  briefDescription: 'briefDescription',
  relatedResolution: 'relatedResolution',
  approval: 'approval',
  totalCosts: 'totalCosts',
  result: 'result',
  resultQuantification: 'resultQuantification',
  type: 'typeTranslation',
  partners: 'partners',
  realizationPlace: 'realizationPlaces',
  followUpPrkkPriorityArea: 'followUpPrkkCombined',
  followUpSrrcrStrategicTarget: 'followUpSrrcrCombined',
  actionPlan: 'actionPlanTranslation',
  developmentState: 'developmentStateTranslation',
  initiative: 'initiativeTranslation',
  preprojectPreparationStart: 'preprojectPreparationStart',
  implementationStart: 'implementationStartYear',
  implementationEnd: 'implementationEndYear',
  implementationLength: 'implementationLength',
  financialEnd: 'financialEnd',
  updated: 'updated',
  sustainabilityPeriodStart: 'sustainabilityPeriodStart',
  sustainabilityPeriodEnd: 'sustainabilityPeriodEnd',
  territorialDecision: 'territorialDecisionTranslation',
  buildingPermit: 'buildingPermitTranslation',
  totalEstimatedCosts: 'totalEstimatedCosts',
  preparationCosts: 'preparationCosts',
  implementationCosts: 'implementationCosts',
  fundingMainSource: 'fundingMainSourceTranslation',
  priority: 'priority'
};

export const mapVisibleColumnsToFieldNames = (visibleColumnIds: string[]): string[] => {
  return visibleColumnIds
    .map((columnId) => COLUMN_MAPPING[columnId])
    .filter((fieldName) => fieldName !== undefined);
};

export const getProjectExportParameter = (
  projectIds: number[],
  exportAll: boolean,
  visibleColumnIds: string[],
  tableChanges?: TableChangesProps,
  filterMyProjects?: boolean
): ProjectExportRequestDto => {
  const columns = mapVisibleColumnsToFieldNames(visibleColumnIds);

  // Always include these columns in export
  const requiredColumns = [
    'budgetData',
    'totalProjectExpenses',
    'expectedIncome',
    'ownResourcesAmount',
    'fundingPercentageBreakdown',
    'bindingFinancialPromiseApproval',
    'projectPreparationApproval',
    'otherTypeApproval',
    'notes'
  ];
  requiredColumns.forEach((col) => {
    if (!columns.includes(col)) {
      columns.push(col);
    }
  });

  return {
    projectIds: projectIds,
    exportAll: exportAll,
    columns: columns,
    globalFilter: tableChanges?.globalFilter || undefined,
    columnFilters: tableChanges?.columnFilters?.length
      ? tableChanges.columnFilters.map((f) => ({ id: f.id, value: f.value as object }))
      : undefined,
    filterMyProjects: filterMyProjects
  };
};

export const getUsersTableFilters = (
  tableChanges: TableChangesProps
): GetUsersPageUserFiltersParameter => {
  return JSON.stringify({
    globalFilter: tableChanges.globalFilter,
    columnFilters: tableChanges.columnFilters
  }) as GetUsersPageUserFiltersParameter;
};

export const getUsersTablePagination = (
  tableChanges: TableChangesProps
): GetProjectsPaginationParameter => {
  return JSON.stringify({
    sorting: tableChanges.sorting,
    pageIndex: tableChanges.pagination.pageIndex,
    pageSize: tableChanges.pagination.pageSize
  }) as GetProjectsPaginationParameter;
};

export const getUserNotificationsTablePagination = (): GetProjectsPaginationParameter => {
  return JSON.stringify({
    sorting: [],
    pageIndex: 0,
    pageSize: 10
  }) as GetProjectsPaginationParameter;
};

const getYear = (date: Date | string): number => {
  if (date instanceof Date) return date.getFullYear();
  return new Date(date).getFullYear();
};

const getYearsBetween = (startYear: number, endYear: number): number[] => {
  const years: number[] = [];
  for (let year = startYear; year <= endYear; year++) {
    years.push(year);
  }
  return years;
};

// Začátek let obou finančních tabulek: dřívější ze zahájení předprojektové přípravy
// a zahájení realizace (nebo to z nich, které je vyplněné).
const getExpensesStartYear = (
  preprojectPreparationStart?: Date | string,
  implementationStart?: Date | string
): number | undefined => {
  if (preprojectPreparationStart && implementationStart) {
    return Math.min(getYear(preprojectPreparationStart), getYear(implementationStart));
  }
  if (preprojectPreparationStart) return getYear(preprojectPreparationStart);
  if (implementationStart) return getYear(implementationStart);
  return undefined;
};

export const getExpensesYears = (
  preprojectPreparationStart?: Date | string,
  implementationStart?: Date | string,
  implementationEnd?: Date | string
): number[] => {
  const startYear = getExpensesStartYear(preprojectPreparationStart, implementationStart);
  const endYear = implementationEnd ? getYear(implementationEnd) : undefined;

  if (startYear !== undefined && endYear !== undefined && endYear >= startYear) {
    return getYearsBetween(startYear, endYear);
  }

  // Fallback: static years when dates are not available
  const currentYear = new Date().getFullYear();
  const lastFiveYears = Array.from({ length: 5 }, (_, index) => currentYear - 4 + index);
  const nextFourYears = Array.from({ length: 4 }, (_, index) => currentYear + index + 1);
  return [...lastFiveYears, ...nextFourYears];
};

export const getYearsRange = (
  startDate?: Date | string,
  endDate?: Date | string
): number[] | null => {
  if (!startDate || !endDate) return null;
  const start = getYear(startDate);
  const end = getYear(endDate);
  if (end < start) return null;
  return getYearsBetween(start, end);
};

/**
 * [DAP-040] Roky 2. tabulky finančního plánu (provozní náklady, náklady na udržitelnost,
 * plánované příjmy dotací ex-post, plánované přijaté zálohy). Začínají stejně jako tabulka
 * výdajů — dřívějším ze zahájení předprojektové přípravy a zahájení realizace, ne datem
 * udržitelnosti, protože dotace ex-post i zálohy přicházejí i v době realizace — a končí
 * ukončením doby udržitelnosti.
 *
 * Bez ukončení udržitelnosti (nebo když leží před začátkem) platí dosavadní chování: období
 * udržitelnosti, jinak `null` a volající použije roky tabulky výdajů.
 */
export const getSustainabilityTableYears = (
  preprojectPreparationStart?: Date | string,
  implementationStart?: Date | string,
  sustainabilityPeriodStart?: Date | string,
  sustainabilityPeriodEnd?: Date | string
): number[] | null => {
  const startYear = getExpensesStartYear(preprojectPreparationStart, implementationStart);
  const endYear = sustainabilityPeriodEnd ? getYear(sustainabilityPeriodEnd) : undefined;

  if (startYear !== undefined && endYear !== undefined && endYear >= startYear) {
    return getYearsBetween(startYear, endYear);
  }

  return getYearsRange(sustainabilityPeriodStart, sustainabilityPeriodEnd);
};

export function isNowBetween(dateFrom?: Date, dateTo?: Date): boolean {
  const now = new Date();
  if (dateFrom && dateTo) {
    return now >= dateFrom && now <= dateTo;
  } else if (dateFrom) {
    return now >= dateFrom;
  } else if (dateTo) {
    return now <= dateTo;
  } else {
    return false;
  }
}

export function truncateString(inputString?: string, maxLength?: number): string {
  if (!inputString) {
    return '';
  }
  if (inputString.length <= (maxLength ?? 30)) {
    return inputString;
  } else {
    return inputString.substring(0, maxLength ?? 30) + '...';
  }
}

export function formatNumberWithCurrency(number: number): string {
  if (!number && number != 0) {
    return '';
  }
  const formattedNumber = number.toLocaleString('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return `${formattedNumber} Kč`;
}
